/**
 * Pricing page — iter27 3-tier model.
 *
 * Tiers:
 * - Free $0  · 2 catalog reads + 1 own plan (no AI)
 * - Starter $49.90 / 49,90 € · 6 self-picked templates + 1 plan with AI
 * - Pro     $99.90 / 99,90 € · Unlimited templates + 10 plans/month with AI
 *
 * Currency follows the UI language: EUR for ES, USD elsewhere.
 *
 * Until Stripe is wired up, the "Activate" button calls the sandbox endpoint
 * directly to set the tier.
 */
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, Sparkles, Lock, Info, ArrowRight, Crown, Zap, AlertCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { PrimaryButton, SecondaryButton, Overline } from "@/components/ui-kit";
import usePageMeta from "@/hooks/usePageMeta";
import { toast } from "sonner";

const COPY = {
  es: {
    eyebrow: "Precios",
    title: "Tres planes. Empieza gratis.",
    subtitle:
      "Construye tu business plan con herramientas IA, 12 plantillas por sector y exportaciones PPTX/PDF. Cancela cuando quieras — tus planes guardados se quedan contigo.",
    sandbox_title: "Modo de prueba",
    sandbox_body:
      "El pago con Stripe se activará en el lanzamiento. Por ahora, los botones activan el plan directamente para que pruebes la experiencia completa.",
    cancellation_title: "¿Qué pasa si cancelo?",
    cancellation_body:
      "Conservas todos tus business plans y plantillas guardadas. Quedan en modo lectura/edición manual: las herramientas IA y la creación de nuevos planes se desactivan hasta que reactives.",
    current_badge: "Tu plan actual",
    activate: "Activar",
    activating: "Activando…",
    downgrade: "Cancelar y bajar a Free",
    back_dashboard: "Ir a mis proyectos",
    most_popular: "Más popular",
    per_month: "/ mes",
    activated_toast: "Plan activado. ¡A construir!",
    cancelled_toast: "Suscripción cancelada. Conservas tus planes pero las herramientas IA quedan desactivadas.",
    footer_q: "¿Tienes preguntas? Vuelve al ",
    footer_catalog: "catálogo",
    free: {
      name: "Free",
      tagline: "Para explorar la plataforma",
      cta: "Crear cuenta gratis",
      cta_logged: "Tu plan actual",
      features: [
        "2 lecturas completas del catálogo",
        "1 business plan propio (edición manual)",
        "Demo Golden Years Abroad completa",
        "Exporta el pitch deck de la demo en PDF",
      ],
    },
    starter: {
      name: "Starter",
      tagline: "Para fundadores con un proyecto en mente",
      cta: "Activar Starter",
      features: [
        "Elige 6 plantillas del catálogo (de 12)",
        "1 business plan propio editable",
        "Asistente IA + entrevista guiada",
        "Auto-guardado y exportación PDF",
        "Pitch deck de inversores (PPTX y PDF)",
        "Branding y links públicos",
      ],
    },
    pro: {
      name: "Pro",
      tagline: "Para consultoras y agencias",
      cta: "Activar Pro",
      features: [
        "Acceso ilimitado a las 12 plantillas",
        "Hasta 10 business plans al mes",
        "Todas las herramientas IA",
        "Stress Test interactivo + Cap Table",
        "Proyecciones financieras a 5 años",
        "Colaboradores y branding avanzado",
      ],
    },
  },
  en: {
    eyebrow: "Pricing",
    title: "Three plans. Start free.",
    subtitle:
      "Build your business plan with AI tools, 12 industry templates and PPTX/PDF exports. Cancel anytime — your saved plans stay with you.",
    sandbox_title: "Sandbox mode",
    sandbox_body:
      "Stripe checkout goes live at launch. For now these buttons activate the plan directly so you can try the full experience.",
    cancellation_title: "What happens if I cancel?",
    cancellation_body:
      "You keep all your business plans and saved templates. They go into read/manual-edit mode: AI tools and new plan creation are disabled until you re-activate.",
    current_badge: "Your current plan",
    activate: "Activate",
    activating: "Activating…",
    downgrade: "Cancel and downgrade to Free",
    back_dashboard: "Go to my projects",
    most_popular: "Most popular",
    per_month: "/ mo",
    activated_toast: "Plan activated. Let's build!",
    cancelled_toast: "Subscription cancelled. Your plans stay, but AI tools are off until you reactivate.",
    footer_q: "Have questions? Back to the ",
    footer_catalog: "catalog",
    free: {
      name: "Free",
      tagline: "Explore the platform",
      cta: "Create free account",
      cta_logged: "Your current plan",
      features: [
        "2 full catalog reads",
        "1 own business plan (manual edits)",
        "Full Golden Years Abroad demo",
        "Export the demo pitch deck as PDF",
      ],
    },
    starter: {
      name: "Starter",
      tagline: "For founders with one project in mind",
      cta: "Activate Starter",
      features: [
        "Pick 6 templates out of 12",
        "1 editable business plan",
        "AI assistant + guided interview",
        "Auto-save and PDF export",
        "Investor pitch deck (PPTX & PDF)",
        "Branding and public share links",
      ],
    },
    pro: {
      name: "Pro",
      tagline: "For consultancies and agencies",
      cta: "Activate Pro",
      features: [
        "Unlimited access to all 12 templates",
        "Up to 10 business plans per month",
        "All AI tools",
        "Interactive Stress Test + Cap Table",
        "5-year financial projections",
        "Collaborators and advanced branding",
      ],
    },
  },
};

function formatPrice(value, lang) {
  if (!value) return lang === "es" ? "0 €" : "$0";
  if (lang === "es") {
    return `${value.toFixed(2).replace(".", ",")} €`;
  }
  return `$${value.toFixed(2)}`;
}

export default function Pricing() {
  const { user, logout, tier: currentTier, refreshSubscription, setUser } = useAuth();
  const { t, lang } = useLang();
  const nav = useNavigate();
  const [busy, setBusy] = useState("");
  const c = COPY[lang === "en" ? "en" : "es"];

  usePageMeta(
    lang === "es"
      ? "Planes y precios — Free · Starter 49,90€ · Pro 99,90€ | Atelier Business"
      : "Pricing — Free · Starter $49.90 · Pro $99.90 | Atelier Business",
    c.subtitle
  );

  async function setTier(targetTier) {
    if (!user) {
      nav("/register");
      return;
    }
    setBusy(targetTier);
    try {
      const res = await api.post("/catalog/me/subscription", { tier: targetTier });
      setUser({
        ...user,
        subscription_tier: res.data.tier,
        subscription_active: res.data.subscription_active,
        had_subscription: res.data.had_subscription ?? user.had_subscription,
      });
      await refreshSubscription();
      if (targetTier === "free") {
        toast.success(c.cancelled_toast);
      } else {
        toast.success(c.activated_toast);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy("");
    }
  }

  const currency = lang === "es" ? "eur" : "usd";
  const starterPrice = currency === "eur" ? 49.9 : 49.9;
  const proPrice = currency === "eur" ? 99.9 : 99.9;

  const cards = [
    {
      key: "free",
      name: c.free.name,
      tagline: c.free.tagline,
      price: 0,
      features: c.free.features,
      accent: false,
      icon: Sparkles,
    },
    {
      key: "starter",
      name: c.starter.name,
      tagline: c.starter.tagline,
      price: starterPrice,
      features: c.starter.features,
      accent: true,
      badge: c.most_popular,
      icon: Zap,
    },
    {
      key: "pro",
      name: c.pro.name,
      tagline: c.pro.tagline,
      price: proPrice,
      features: c.pro.features,
      accent: false,
      icon: Crown,
    },
  ];

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <header className="border-b border-[#E8E6E1] bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-5 flex items-center justify-between">
          <Link to="/" className="text-xl tracking-tight text-[#1A1A1A]" style={{ fontFamily: '"Playfair Display", serif', fontWeight: 700 }} data-testid="brand-link">
            Atelier <span className="italic font-normal text-[#B89154]">Business</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/catalog" className="text-sm text-[#5C5B5A] hover:text-[#1A1A1A]" data-testid="header-catalog-link">
              {t("catalog.go_to_catalog")}
            </Link>
            <LanguageSwitcher />
            {user ? (
              <button onClick={logout} className="text-sm text-[#5C5B5A] hover:text-[#1A1A1A]" data-testid="logout-btn">
                {t("auth.logout")}
              </button>
            ) : (
              <Link to="/login" className="text-sm text-[#5C5B5A] hover:text-[#1A1A1A]" data-testid="login-link">
                {t("auth.login")} →
              </Link>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 lg:px-12 py-12 lg:py-20">
        <div className="text-center max-w-2xl mx-auto">
          <Overline>{c.eyebrow}</Overline>
          <h1
            className="mt-4 text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#1A1A1A] leading-none"
            style={{ fontFamily: '"Playfair Display", serif', fontWeight: 700 }}
            data-testid="pricing-title"
          >
            {c.title}
          </h1>
          <p className="mt-5 text-base text-[#5C5B5A]">{c.subtitle}</p>
        </div>

        {user && (
          <div className="mt-10 max-w-2xl mx-auto bg-[#FBFAF7] border border-[#E8E6E1] rounded-sm p-4 flex items-start gap-3 text-sm" data-testid="sandbox-notice">
            <Info className="h-4 w-4 text-[#B89154] mt-0.5 shrink-0" strokeWidth={1.5} />
            <div>
              <span className="text-[10px] uppercase tracking-[0.2em] text-[#B89154] font-medium block mb-1">
                {c.sandbox_title}
              </span>
              <p className="text-[#5C5B5A]">{c.sandbox_body}</p>
            </div>
          </div>
        )}

        <div className="mt-12 grid md:grid-cols-3 gap-6 items-stretch">
          {cards.map((card) => {
            const Icon = card.icon;
            const isCurrent = currentTier === card.key;
            const isLoading = busy === card.key;
            return (
              <div
                key={card.key}
                className={`relative bg-white border rounded-sm p-8 flex flex-col ${
                  card.accent ? "border-2 border-[#2563EB] bg-[#FBFAF7]" : "border-[#E8E6E1]"
                }`}
                data-testid={`pricing-${card.key}-card`}
              >
                {card.badge && (
                  <span className="absolute -top-3 left-8 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] bg-[#B89154] text-white px-3 py-1 rounded-sm">
                    <Sparkles className="h-3 w-3" strokeWidth={2} />
                    {card.badge}
                  </span>
                )}
                {isCurrent && (
                  <span
                    className={`absolute -top-3 right-8 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-white px-3 py-1 rounded-sm ${
                      card.accent ? "bg-[#2563EB]" : "bg-[#8A8886]"
                    }`}
                    data-testid={`${card.key}-current-badge`}
                  >
                    <Check className="h-3 w-3" strokeWidth={2} />
                    {c.current_badge}
                  </span>
                )}

                <div className="flex items-center gap-2">
                  <Icon className={`h-5 w-5 ${card.accent ? "text-[#2563EB]" : "text-[#8A8886]"}`} strokeWidth={1.5} />
                  <Overline>
                    <span className={card.accent ? "text-[#2563EB]" : ""}>{card.tagline}</span>
                  </Overline>
                </div>
                <h2 className="mt-3 text-3xl text-[#1A1A1A]" style={{ fontFamily: '"Playfair Display", serif', fontWeight: 600 }}>
                  {card.name}
                </h2>
                <div className="mt-4 flex items-baseline gap-2">
                  <span
                    className="text-5xl text-[#1A1A1A]"
                    style={{ fontFamily: '"Playfair Display", serif', fontWeight: 700 }}
                    data-testid={`${card.key}-price`}
                  >
                    {formatPrice(card.price, lang)}
                  </span>
                  {card.price > 0 && (
                    <span className="text-sm text-[#5C5B5A]">{c.per_month}</span>
                  )}
                </div>
                <ul className="mt-8 space-y-3 flex-1">
                  {card.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm">
                      <Check className={`h-4 w-4 shrink-0 mt-0.5 ${card.accent ? "text-[#2563EB]" : "text-[#5C5B5A]"}`} strokeWidth={2} />
                      <span className="text-[#1A1A1A]">{f}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-8">
                  {!user ? (
                    card.key === "free" ? (
                      <Link to="/register">
                        <SecondaryButton className="w-full justify-center" testId={`${card.key}-cta-btn`}>
                          {c.free.cta}
                        </SecondaryButton>
                      </Link>
                    ) : (
                      <Link to="/register">
                        <PrimaryButton className="w-full" testId={`${card.key}-cta-btn`}>
                          <Lock className="h-4 w-4" strokeWidth={1.5} />
                          {lang === "es" ? "Crear cuenta y activar" : "Sign up and activate"}
                        </PrimaryButton>
                      </Link>
                    )
                  ) : isCurrent ? (
                    <Link to="/dashboard">
                      <SecondaryButton className="w-full justify-center" testId={`${card.key}-go-dashboard-btn`}>
                        {c.back_dashboard}
                        <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
                      </SecondaryButton>
                    </Link>
                  ) : card.key === "free" ? (
                    <SecondaryButton
                      onClick={() => setTier("free")}
                      disabled={isLoading || busy}
                      className="w-full justify-center"
                      testId="downgrade-free-btn"
                    >
                      {isLoading ? c.activating : c.downgrade}
                    </SecondaryButton>
                  ) : (
                    <PrimaryButton
                      onClick={() => setTier(card.key)}
                      disabled={isLoading || busy}
                      className="w-full"
                      testId={`activate-${card.key}-btn`}
                    >
                      {isLoading ? c.activating : (
                        <>
                          <Lock className="h-4 w-4" strokeWidth={1.5} />
                          {card.key === "starter" ? c.starter.cta : c.pro.cta}
                        </>
                      )}
                    </PrimaryButton>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Cancellation policy */}
        <div className="mt-12 max-w-3xl mx-auto bg-white border border-[#E8E6E1] rounded-sm p-6 flex items-start gap-4" data-testid="cancellation-policy">
          <AlertCircle className="h-5 w-5 text-[#B89154] mt-0.5 shrink-0" strokeWidth={1.5} />
          <div>
            <h3
              className="text-lg text-[#1A1A1A]"
              style={{ fontFamily: '"Playfair Display", serif', fontWeight: 600 }}
            >
              {c.cancellation_title}
            </h3>
            <p className="mt-2 text-sm text-[#5C5B5A]">{c.cancellation_body}</p>
          </div>
        </div>

        <div className="mt-12 text-center text-sm text-[#5C5B5A]">
          {c.footer_q}
          <Link to="/catalog" className="text-[#2563EB] underline" data-testid="footer-catalog-link">{c.footer_catalog}</Link>
          {lang === "es" ? " o " : " or "}
          {user ? (
            <Link to="/dashboard" className="text-[#2563EB] underline">
              {lang === "es" ? "vuelve a tus proyectos" : "back to your projects"}
            </Link>
          ) : (
            <Link to="/register" className="text-[#2563EB] underline">
              {lang === "es" ? "crear cuenta" : "create account"}
            </Link>
          )}
          .
        </div>
      </main>
    </div>
  );
}
